import { useEffect, useState } from 'react';

interface LocalStorageStateProps {
  key: string;
  defaultValue?: string;
  removeOnClose?: boolean;
}

export const useLocalStorageState = ({
  key,
  defaultValue,
  removeOnClose,
}: LocalStorageStateProps) => {
  const [value, setValue] = useState<string>(() => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error);
      return defaultValue;
    }
  });

  const setStoredValue = (newValue: string) => {
    try {
      setValue(newValue);
      localStorage.setItem(key, JSON.stringify(newValue));
    } catch (error) {
      console.warn(`Error setting localStorage key "${key}":`, error);
      setValue(newValue);
    }
  };

  useEffect(() => {
    if (removeOnClose) {
      // Generate a unique ID for this tab instance
      const tabId = Math.random().toString(36).substr(2, 9);

      // Store the tab ID in localStorage to track this tab
      const tabKey = `${key}_tab_${tabId}`;
      localStorage.setItem(tabKey, 'active');

      const removeFromStorage = () => {
        // Remove the tab tracking key
        localStorage.removeItem(tabKey);

        // Check if this is the last tab using this localStorage key
        const activeTabs = Object.keys(localStorage).filter(
          (k) =>
            k.startsWith(`${key}_tab_`) && localStorage.getItem(k) === 'active',
        );

        // If no other tabs are using this key, remove the main localStorage item
        if (activeTabs.length === 0) {
          localStorage.removeItem(key);
        }
      };

      // Listen for storage events to detect when other tabs are closed
      const handleStorage = (event: StorageEvent) => {
        if (event.key === tabKey && event.newValue === null) {
          // Another tab was closed, but we don't need to do anything
          // Just let the cleanup happen naturally
        }
      };

      // Listen for tab close events
      const handleBeforeUnload = () => {
        removeFromStorage();
      };

      window.addEventListener('storage', handleStorage);
      window.addEventListener('beforeunload', handleBeforeUnload);

      // Cleanup function to remove event listeners
      return () => {
        window.removeEventListener('storage', handleStorage);
        window.removeEventListener('beforeunload', handleBeforeUnload);
        // Also clean up the tab tracking key when component unmounts
        localStorage.removeItem(tabKey);
      };
    }

    // Return undefined when removeOnClose is false
    return undefined;
  }, [removeOnClose, key]);

  return [value, setStoredValue] as const;
};
